export interface ConnectedApp {
  id: string
  name: string
  logoColor: string
  status: 'Connected' | 'Needs reauth' | 'Not connected'
  accountName?: string
  lastSync?: string
}

export const mockApps: ConnectedApp[] = [
  {
    id: 'platform',
    name: 'Platform',
    logoColor: 'bg-blue-600',
    status: 'Connected',
    accountName: 'Bank Rakyat Merchant',
    lastSync: 'just now',
  },
  {
    id: 'quickbooks',
    name: 'QuickBooks Online',
    logoColor: 'bg-green-600',
    status: 'Connected',
    accountName: 'Acme Corp Sdn Bhd',
    lastSync: '2 mins ago',
  },
  {
    id: 'xero',
    name: 'Xero',
    logoColor: 'bg-cyan-500',
    status: 'Needs reauth',
    accountName: 'Acme Corp (SG)',
  },
  {
    id: 'sage',
    name: 'Sage Intacct',
    logoColor: 'bg-emerald-700',
    status: 'Not connected',
  },
]

export function getApp(id: string): ConnectedApp | undefined {
  return mockApps.find((a) => a.id === id)
}

export interface AutomationRule {
  id: string
  name: string
  status: 'Active' | 'Paused' | 'Error' | 'Draft'
  triggerApp: string
  triggerEvent: string
  actionApp: string
  actionEvent: string
  runs30d: number
  successRate: number
  lastRunTime?: string
}

export const mockAutomations: AutomationRule[] = [
  {
    id: 'rule_1a2b3c',
    name: 'Sync Payments to QuickBooks',
    status: 'Active',
    triggerApp: 'platform',
    triggerEvent: 'Payment Succeeded',
    actionApp: 'quickbooks',
    actionEvent: 'Reconcile Invoice',
    runs30d: 1245,
    successRate: 99.8,
    lastRunTime: '2026-06-15T08:42:00Z',
  },
  {
    id: 'rule_4d5e6f',
    name: 'Notify on Dispute Opened',
    status: 'Active',
    triggerApp: 'platform',
    triggerEvent: 'Dispute Opened',
    actionApp: 'platform',
    actionEvent: 'Send Email Notification',
    runs30d: 12,
    successRate: 100,
    lastRunTime: '2026-06-14T14:20:00Z',
  },
  {
    id: 'rule_7g8h9i',
    name: 'Xero Invoice → Payment Link',
    status: 'Error',
    triggerApp: 'xero',
    triggerEvent: 'Invoice Created',
    actionApp: 'platform',
    actionEvent: 'Send Payment Link',
    runs30d: 450,
    successRate: 85.5,
    lastRunTime: '2026-06-15T07:15:00Z',
  },
  {
    id: 'rule_0j1k2l',
    name: 'High Value Payment Alert',
    status: 'Paused',
    triggerApp: 'platform',
    triggerEvent: 'Payment Received',
    actionApp: 'platform',
    actionEvent: 'Send SMS',
    runs30d: 0,
    successRate: 0,
  },
  {
    id: 'rule_3m4n5o',
    name: 'Sage Contact Sync',
    status: 'Draft',
    triggerApp: 'platform',
    triggerEvent: 'Customer Created',
    actionApp: 'sage',
    actionEvent: 'Create Contact',
    runs30d: 0,
    successRate: 0,
  },
]

export function getAutomation(id: string): AutomationRule | undefined {
  return mockAutomations.find((a) => a.id === id)
}

export interface AutomationRun {
  id: string
  automationId: string
  automationName: string
  timestamp: string
  status: 'Success' | 'Failed' | 'Filtered' | 'Retrying'
  durationMs: number
  triggerEventId: string
  input: Record<string, unknown>
  output?: Record<string, unknown>
  error?: string
}

export const mockRuns: AutomationRun[] = [
  {
    id: 'run_9x8y7z',
    automationId: 'rule_1a2b3c',
    automationName: 'Sync Payments to QuickBooks',
    timestamp: '2026-06-15T08:42:00Z',
    status: 'Success',
    durationMs: 450,
    triggerEventId: 'evt_pay_123456',
    input: {
      payment_id: 'pay_123456',
      amount: 1500.0,
      currency: 'MYR',
      status: 'succeeded',
      metadata: { invoice_id: 'INV-2026-001' },
    },
    output: {
      qb_payment_id: '145',
      qb_invoice_id: 'INV-2026-001',
      status: 'reconciled',
    },
  },
  {
    id: 'run_6w5v4u',
    automationId: 'rule_7g8h9i',
    automationName: 'Xero Invoice → Payment Link',
    timestamp: '2026-06-15T07:15:00Z',
    status: 'Failed',
    durationMs: 1200,
    triggerEventId: 'evt_xero_inv_789',
    input: {
      invoice_id: 'INV-0042',
      amount: 5000.0,
      currency: 'SGD',
      customer: { email: 'client@example.com' },
    },
    error:
      'Xero API Error: token expired (401). Reauthorize the Xero connection.',
  },
  {
    id: 'run_3t2s1r',
    automationId: 'rule_1a2b3c',
    automationName: 'Sync Payments to QuickBooks',
    timestamp: '2026-06-15T08:10:00Z',
    status: 'Filtered',
    durationMs: 45,
    triggerEventId: 'evt_pay_123455',
    input: {
      payment_id: 'pay_123455',
      amount: 50.0,
      currency: 'MYR',
      status: 'succeeded',
    },
    output: { reason: 'Condition not met: amount >= 100' },
  },
  {
    id: 'run_2q1p0o',
    automationId: 'rule_4d5e6f',
    automationName: 'Notify on Dispute Opened',
    timestamp: '2026-06-14T14:20:00Z',
    status: 'Retrying',
    durationMs: 890,
    triggerEventId: 'evt_dsp_555',
    input: {
      dispute_id: 'dsp_555',
      amount: 320.0,
      currency: 'MYR',
      reason: 'fraudulent',
    },
    output: { attempt: 2, next_retry_in: '5m' },
  },
]

export interface AutomationTemplate {
  id: string
  name: string
  description: string
  triggerApp: string
  triggerEvent: string
  actionApp: string
  actionEvent: string
  popular: boolean
}

export const mockTemplates: AutomationTemplate[] = [
  {
    id: 'tpl_1',
    name: 'Invoice → Payment Link',
    description:
      'When an invoice is created in QuickBooks, send a payment link to the customer.',
    triggerApp: 'quickbooks',
    triggerEvent: 'Invoice Created',
    actionApp: 'platform',
    actionEvent: 'Send Payment Link',
    popular: true,
  },
  {
    id: 'tpl_2',
    name: 'Auto-Reconcile Payments',
    description:
      'When a payment is received, reconcile the amount against the matching invoice in QuickBooks.',
    triggerApp: 'platform',
    triggerEvent: 'Payment Received',
    actionApp: 'quickbooks',
    actionEvent: 'Reconcile Invoice',
    popular: true,
  },
  {
    id: 'tpl_3',
    name: 'Failed Payment Alert',
    description:
      'Payment failed → notify customer and flag the invoice in Xero.',
    triggerApp: 'platform',
    triggerEvent: 'Payment Failed',
    actionApp: 'xero',
    actionEvent: 'Flag Invoice',
    popular: false,
  },
  {
    id: 'tpl_4',
    name: 'Settlement Journal Entry',
    description: 'Settlement completed → post a reconciliation entry in Sage.',
    triggerApp: 'platform',
    triggerEvent: 'Payout Completed',
    actionApp: 'sage',
    actionEvent: 'Post Journal Entry',
    popular: false,
  },
]

export const PLATFORM_EVENTS = [
  'Payment Received',
  'Payment Succeeded',
  'Payment Failed',
  'Payment Link Paid',
  'Refund Issued',
  'Dispute Opened',
  'Payout Completed',
  'Customer Created',
]

export const APP_EVENTS = [
  'Invoice Created',
  'Invoice Updated',
  'Invoice Voided',
  'Bill Created',
  'Contact Created',
]

export const PLATFORM_ACTIONS = [
  'Send Payment Link',
  'Create Payment Request',
  'Send Email Notification',
  'Send SMS',
  'Send Webhook',
  'Capture Payment',
  'Refund Payment',
  'Tag Transaction',
]

export const APP_ACTIONS = [
  'Reconcile Invoice',
  'Mark Invoice as Paid',
  'Create Payment Record',
  'Create Contact',
  'Update Contact',
  'Post Journal Entry',
]

export const SAMPLE_TRIGGER_PAYLOAD: Record<string, unknown> = {
  id: 'evt_pay_123456',
  type: 'payment.succeeded',
  data: {
    payment_id: 'pay_123456',
    amount: 1500.0,
    currency: 'MYR',
    status: 'succeeded',
    customer: { id: 'cus_789', email: 'client@example.com' },
    metadata: { invoice_id: 'INV-2026-001' },
  },
}
